import React, { useLayoutEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LayoutGrid, FileCheck, Activity, XCircle, CheckCircle, Pause, Hourglass,
  Archive, FileEdit, Ban, Trash2, Users, ShieldCheck, BadgeCheck, DollarSign,
  BarChart3, KeyRound, UserPlus, TrendingUp, ClipboardCheck, Store,
  ArrowLeftRight, Settings, Newspaper, Package, CircleUserRound, Briefcase,
  ScrollText, AlertCircle, Mail, LogOut, Landmark,
} from "lucide-react";
import LogoDark from "../../assets/logo/logo-dark.svg";
import { usePermissions } from "../../context/permissionContext";
import { validatePermission } from "../../utils/permissionUtils";
import { useRole } from "../../hooks/useRole";
import { roleLabel } from "../../utils/roleLabels";
import { sortRoles } from "../../utils/complianceRoles";
import useFocusTrap from "../../hooks/useFocusTrap";

const itemClass = ({ isActive }) => "sb__item" + (isActive ? " sb__item--on" : "");

const SbItem = ({ to, end, Icon, label }) => (
  <NavLink to={to} end={end} className={itemClass}>
    <Icon />
    <span>{label}</span>
  </NavLink>
);

const Sidebar = ({ isOpen, onClose, isDrawerMode }) => {
  const navigate = useNavigate();
  const adminLogin = useSelector((state) => state.adminLogin);
  const { user } = adminLogin || {};
  const { permissions, permissionsMapping } = usePermissions();
  const { roles, hasRole, canDecide, canReview } = useRole();

  const isAD = hasRole("AD");
  // A session that has not resolved a role yet still gets the admin nav, which
  // is the pre-existing behaviour for the seconds before the login payload lands.
  const isAdmin = roles.length === 0 || isAD;
  // CONSULTANT sessions live in the BDC ("Business dev") workspace. The API
  // re-shadowed consultants' legacy roleId onto the canonical CONSULTANT row,
  // so their login payload now carries CONSULTANT; treat them as BDC here so
  // the Business-dev nav group renders (BDCReferrals still branches on its own
  // hasRole('CONSULTANT') for the intra-page differences).
  const isBDC = hasRole("BDC", "CONSULTANT");

  // In drawer mode (≤1024px) the open sidebar is a modal overlay: trap focus,
  // restore it to the opener on close, and close on Escape (all via useFocusTrap).
  const drawerActive = isDrawerMode && isOpen;
  const ref = useFocusTrap({ active: drawerActive, onClose });

  // A closed off-canvas drawer is only hidden via `transform`, so it stays in
  // the tab order / a11y tree — mark it inert when closed in drawer mode. On
  // desktop the sidebar is a permanent rail and must never be inert. React 17
  // ignores the JSX `inert` prop, so set it imperatively; useLayoutEffect runs
  // before the focus trap's passive effect (which moves focus inside on open).
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.inert = isDrawerMode && !isOpen;
  }, [isDrawerMode, isOpen, ref]);

  const canSeeReviewHub =
    validatePermission("Campaign - Initial Approval:View", permissions, permissionsMapping) ||
    validatePermission("Campaign - For Review:View", permissions, permissionsMapping);

  // The People group is admin nav, but two of its screens are permission-gated
  // routes (App.js) rather than AD-only ones, so a non-admin session granted
  // them had no way to reach them from the nav. Gate the links on the same
  // permission strings the routes use.
  const canSeeUsers = validatePermission(
    "Users Management - Users:View",
    permissions,
    permissionsMapping
  );
  const canSeeAccreditedInvestors = validatePermission(
    "Accredited Investors:View",
    permissions,
    permissionsMapping
  );
  const showPeople = isAdmin || canSeeUsers || canSeeAccreditedInvestors;

  // One Compliance group for every role that works the queues. A decider (DR,
  // CCO, UDP) gets their own overview and credentials; a reviewer (CCO, UDP)
  // also gets the decision review and the two oversight screens; an AD gets
  // the queues for oversight plus those same two screens. Each label appears
  // exactly once, whatever combination of roles the session holds.
  const showCompliance = canDecide || isAD;
  const showComplianceOversight = isAD || canReview;

  const logoutHandler = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("admin");
    localStorage.removeItem("navOpen");
    localStorage.removeItem("navOpen3");
    localStorage.removeItem("navOpen5");
    navigate("/login");
  };

  const firstname = user?.data?.firstname || "";
  const lastname = user?.data?.lastname || "";
  const emailLocal = user?.data?.email?.split("@")[0] || "";
  const fullName = [firstname, lastname].filter(Boolean).join(" ") || emailLocal || "Admin";
  const initials = (() => {
    const f = firstname.charAt(0);
    const l = lastname.charAt(0);
    if (f && l) return (f + l).toUpperCase();
    if (firstname) return firstname.slice(0, 2).toUpperCase();
    if (emailLocal) return emailLocal.slice(0, 2).toUpperCase();
    return "··";
  })();
  // A session can hold several roles, and the footer is where the user checks
  // which hat they are wearing, so name all of them in rank order rather than
  // picking one. Falls back to a friendly default when no role has resolved.
  const roleDisplay = roles.length
    ? sortRoles(roles).map(roleLabel).join(" / ")
    : "Platform Manager";

  return (
    <aside id="app-sidebar" className={"sb" + (isOpen ? " sb--open" : "")} ref={ref}>
      <div className="sb__brand">
        <img src={LogoDark} alt="Roots Funding" className="sb__brand-logo" />
      </div>

      <div className="sb__group">Overview</div>
      <SbItem to="/" end Icon={LayoutGrid} label="Dashboard" />

      {showCompliance && (
        <>
          <div className="sb__group">Compliance</div>
          {canDecide && <SbItem to="/dr" end Icon={LayoutGrid} label="Reviewer overview" />}
          {canDecide && (
            <SbItem to="/dr/credentials" Icon={CircleUserRound} label="My credentials" />
          )}
          <SbItem to="/dr/approvals" Icon={UserPlus} label="Investor approvals" />
          <SbItem to="/dr/suitability" Icon={ClipboardCheck} label="Suitability" />
          <SbItem to="/dr/deals" Icon={Briefcase} label="Deals review" />
          <SbItem to="/dr/audit" Icon={ScrollText} label="Review audit trail" />
          {/* An AD reaches this route read-only (RoleGuard admits AD), so
              withholding the link only meant navigating to it by hand. */}
          {(canReview || isAD) && (
            <SbItem to="/compliance/decisions" Icon={FileCheck} label="Decision review" />
          )}
          {showComplianceOversight && (
            <SbItem
              to="/ad/dr-oversight/credentials"
              Icon={CircleUserRound}
              label="DR credentials"
            />
          )}
          {showComplianceOversight && (
            <SbItem to="/ad/dr-oversight/sla-breaches" Icon={AlertCircle} label="SLA breaches" />
          )}
        </>
      )}

      {isBDC && (
        <>
          <div className="sb__group">Business dev</div>
          <SbItem to="/bdc" end Icon={LayoutGrid} label="BDC overview" />
          <SbItem to="/bdc/network" Icon={Users} label="My network" />
          <SbItem to="/bdc/referrals" Icon={TrendingUp} label="Growth tools" />
          <SbItem to="/bdc/payouts" Icon={DollarSign} label="Payouts & analytics" />
        </>
      )}

      {/* Platform oversight is AD nav, except the audit log and the compliance
          reports: a reviewer (CCO, UDP) needs both to do the reviewing, and
          the routes admit them. */}
      {(isAD || canReview) && (
        <>
          <div className="sb__group">Platform oversight</div>
          {isAD && (
            <>
              <SbItem to="/ad/consultant-requests" Icon={ClipboardCheck} label="Consultant approvals" />
              <SbItem to="/ad/commission-rates" Icon={DollarSign} label="Commission rates" />
              <SbItem to="/ad/bdc-oversight/claims" Icon={UserPlus} label="Manual claims" />
              <SbItem to="/ad/bdc-oversight/attribution" Icon={Settings} label="Attribution editor" />
              <SbItem to="/ad/payments/pending" Icon={DollarSign} label="Payments (bank)" />
            </>
          )}
          <SbItem to="/ad/reports/compliance" Icon={BarChart3} label="Compliance reports" />
          {isAD && (
            <>
              <SbItem to="/ad/payouts" Icon={DollarSign} label="Payouts (AD)" />
              <SbItem to="/ad/reports/email-log" Icon={Mail} label="Email log" />
              <SbItem to="/ad/marketing/campaigns" Icon={Mail} label="Marketing campaigns" />
            </>
          )}
          <SbItem to="/ad/reports/audit" Icon={ScrollText} label="Audit log" />
        </>
      )}

      {isAdmin && (
        <>
          <div className="sb__group">Pipeline</div>
          {canSeeReviewHub && <SbItem to="/review-hub" Icon={FileCheck} label="Review hub" />}
          <SbItem to="/active-campaign" Icon={Activity} label="Active" />
          <SbItem to="/rejected-campaign" Icon={XCircle} label="Rejected" />
          <SbItem to="/completed-campaign" Icon={CheckCircle} label="Completed" />
          <SbItem to="/paused-campaign" Icon={Pause} label="Paused" />
          <SbItem to="/coming-soon-campaign" Icon={Hourglass} label="Upcoming" />
          <SbItem to="/closed-campaign" Icon={Archive} label="Closed" />
          <SbItem to="/draft-campaign" Icon={FileEdit} label="Draft" />
          <SbItem to="/cancelled-campaign" Icon={Ban} label="Cancelled" />
          <SbItem to="/deleted-campaign" Icon={Trash2} label="Deleted" />
        </>
      )}

      {showPeople && (
        <>
          <div className="sb__group">People</div>
          {(isAdmin || canSeeUsers) && <SbItem to="/users" Icon={Users} label="Users" />}
          {isAdmin && (
            <>
              <SbItem to="/manage-role" Icon={ShieldCheck} label="System roles" />
              <SbItem to="/manage-designation" Icon={BadgeCheck} label="Designations" />
              <SbItem to="/commission" Icon={DollarSign} label="Commissions" />
              <SbItem to="/designation-report" Icon={BarChart3} label="Designation report" />
              <SbItem to="/manage-permissions" Icon={KeyRound} label="Permissions" />
            </>
          )}
          {(isAdmin || canSeeAccreditedInvestors) && (
            <SbItem to="/accredited-investors" Icon={UserPlus} label="Accredited investors" />
          )}
        </>
      )}

      {isAdmin && (
        <>
          <div className="sb__group">Money</div>
          <SbItem to="/investment" Icon={TrendingUp} label="Investments" />
          <SbItem to="/ach-files" Icon={Landmark} label="ACH files" />
          <SbItem to="/wire-reconciliation" Icon={ArrowLeftRight} label="Wire reconciliation" />
          <SbItem to="/campaign-closings" Icon={FileCheck} label="Campaign closings" />
          <SbItem to="/suitability-overview" Icon={ClipboardCheck} label="Investment payment ledger" />

          {process.env.REACT_APP_SECONDARY_MARKET_ENABLED === 'true' && (
            <>
              <div className="sb__group">Marketplace</div>
              <SbItem to="/secondary-listings" Icon={Store} label="Listings" />
              <SbItem to="/secondary-transactions" Icon={ArrowLeftRight} label="Transactions" />
              <SbItem to="/secondary-config" Icon={Settings} label="Configuration" />
            </>
          )}

          <div className="sb__group">Content</div>
          <SbItem to="/news" Icon={Newspaper} label="News" />
          <SbItem to="/inventme" Icon={Package} label="InventMe" />
          <SbItem to="/careers" Icon={Briefcase} label="Careers" />
        </>
      )}

      <div className="sb__foot">
        <span className="sb__foot-ava">{initials}</span>
        <div>
          <div className="sb__foot-name">{fullName}</div>
          <div className="sb__foot-role">{roleDisplay}</div>
        </div>
        <div className="sb__foot-actions">
          <button
            className="sb__foot-icon"
            aria-label="Profile"
            onClick={() => navigate("/profile")}
            type="button"
          >
            <Settings />
          </button>
          <button
            className="sb__foot-icon"
            aria-label="Logout"
            onClick={logoutHandler}
            type="button"
          >
            <LogOut />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
